import React, { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || ''; // f.eks. https://ryfylkeutleie.no/api

export default function Checklist() {
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noteByTask, setNoteByTask] = useState({});
  const [error, setError] = useState('');

  // Hjelper: hent riktig nøkkel for en task (vmId > id)
  const taskKey = useCallback((t) => t?.vmId ?? t?.vehicleMaintenanceId ?? t?.id, []);

  // Hent employees/vehicles
  useEffect(() => {
    (async () => {
      try {
        const [eRes, vRes] = await Promise.all([
          fetch(`${API}/check/employees`),
          fetch(`${API}/check/vehicles`),
        ]);
        const eJson = eRes.ok ? await eRes.json() : [];
        const vJson = vRes.ok ? await vRes.json() : [];
        setEmployees(Array.isArray(eJson) ? eJson : []);
        setVehicles(Array.isArray(vJson) ? vJson : []);
      } catch {
        setEmployees([]);
        setVehicles([]);
      }
    })();
  }, []);

  // Hent tasks for valgt vehicle
  useEffect(() => {
    if (!selectedVehicle) {
      setTasks([]);
      return;
    }
    setLoading(true);
    setError('');
    (async () => {
      try {
        const r = await fetch(`${API}/check/vehicle/${selectedVehicle}/tasks`);
        if (!r.ok) throw new Error('Kunne ikke hente oppgaver');
        const data = await r.json();

        // Sortér etter forfallsdato (eldst først), deretter status
        const sorted = [...(Array.isArray(data) ? data : [])].sort((a, b) => {
          const da = new Date(a.next_due).getTime() || 0;
          const db = new Date(b.next_due).getTime() || 0;
          if (da !== db) return da - db;
          const rank = (s) => (s === 'overdue' ? 0 : s === 'soon' ? 1 : 2);
          return rank(a.status) - rank(b.status);
        });

        setTasks(sorted);
      } catch (e) {
        setTasks([]);
        setError(e.message || 'Feil ved henting av oppgaver');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedVehicle]);

  async function markDone(vmIdOrTaskId) {
    setError('');
    try {
      const res = await fetch(`${API}/check/vehicle-tasks/${vmIdOrTaskId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({
          employeeId: selectedEmployee || null,
          notes: noteByTask[vmIdOrTaskId] || ''
        })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Feil ved lagring');

      // Refetch tasks etter oppdatering
      const freshRes = await fetch(`${API}/check/vehicle/${selectedVehicle}/tasks`);
      const fresh = freshRes.ok ? await freshRes.json() : [];
      setTasks(Array.isArray(fresh) ? fresh : []);
      setNoteByTask(prev => ({ ...prev, [vmIdOrTaskId]: '' }));
    } catch (e) {
      setError(e.message || 'Noe gikk galt');
    }
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold text-[#000099]">Vedlikehold – Checklist</h1>

      {/* Steg 1: Velg ansatt (med bilde) */}
      <section className="bg-white rounded-xl shadow p-4">
        <div className="font-semibold mb-3">1) Velg hvem du er</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {employees.map(e => {
            const img = e.image_url || '/icons/user-placeholder.png';
            const active = Number(selectedEmployee) === Number(e.id);
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => setSelectedEmployee(e.id)}
                className={`flex items-center gap-3 p-3 rounded border transition
                  ${active ? 'border-[#000099] bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <img
                  src={img}
                  alt={e.name || 'Ansatt'}
                  className="w-10 h-10 rounded-full object-cover"
                  onError={(ev) => { ev.currentTarget.src = '/icons/user-placeholder.png'; }}
                />
                <span className="text-sm font-medium">{e.name || 'Ukjent'}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Steg 2: Velg kjøretøy */}
      <section className={`bg-white rounded-xl shadow p-4 ${!selectedEmployee ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="font-semibold mb-2">2) Velg kjøretøy</div>
        <select
          className="w-full border px-3 py-2 rounded"
          value={selectedVehicle || ''}
          onChange={e => setSelectedVehicle(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">— Velg kjøretøy —</option>
          {vehicles.map(v => (
            <option key={v.id} value={v.id}>
              {v.name}{v.plate ? ` (${v.plate})` : ''}
            </option>
          ))}
        </select>
      </section>

      {/* Steg 3: Oppgaver */}
      <section className={`bg-white rounded-xl shadow p-4 ${!selectedVehicle ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="font-semibold mb-2">3) Oppgaver for valgt kjøretøy</div>
        {loading && <div>Laster…</div>}
        {error && <div className="text-red-600 mb-2">{error}</div>}
        {!loading && tasks.length === 0 && <div>Ingen oppgaver funnet.</div>}

        <ul className="space-y-3">
          {tasks.map(t => {
            const key = taskKey(t);
            const when = t.next_due ? new Date(t.next_due) : null;
            return (
              <li key={key} className="border rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{t.title}</div>
                    <div className="text-xs text-gray-600">
                      Neste frist: {when ? when.toLocaleDateString('no-NO') : '–'} &middot;&nbsp;
                      Status: {t.status === 'overdue' ? 'Forfalt' : t.status === 'soon' ? 'Snart forfalt' : 'OK'}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    t.status === 'overdue' ? 'bg-red-100 text-red-700'
                    : t.status === 'soon' ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-700'
                  }`}>
                    {t.status || 'ok'}
                  </span>
                </div>

                {t.description ? (
                  <div className="mt-2 text-sm text-gray-700">{t.description}</div>
                ) : null}

                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Notat (valgfritt)"
                    value={noteByTask[key] || ''}
                    onChange={e => setNoteByTask(prev => ({ ...prev, [key]: e.target.value }))}
                    className="flex-1 border px-3 py-1 rounded"
                  />
                  <button
                    onClick={() => markDone(key)}
                    className="bg-[#000099] text-white px-4 py-2 rounded hover:bg-blue-900"
                  >
                    Merk som gjort
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
